<script setup lang="ts">
  import { ref } from 'vue';
  import PosUiBadge from '@/components/pos-v2/ui/PosUiBadge.vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import type { TCaptureOrigin } from '@/composition/menu-import/use-menu-capture';

  /**
   * The ways in of the menu source screen (plan §4.3): on phones «Sacar
   * fotos a su carta» (camera) and «Subir fotos o PDF»; on larger screens a
   * drop zone for photos, PDF and spreadsheets and «Escribirlo o pegarlo».
   * With the AI off, one «Cargarlo a mano» card instead. The file pickers are
   * real hidden `<input type="file">` elements opened with `click()`.
   */
  const FILE_ACCEPT =
    'image/*,.heic,.heif,application/pdf,.pdf,.xlsx,.xls,.ods,.csv,text/csv';

  const props = defineProps<{
    phone: boolean;
    aiEnabled: boolean;
    disabled: boolean;
  }>();
  const emit = defineEmits<{
    (e: 'files', files: File[], origin: TCaptureOrigin): void;
    (e: 'paste'): void;
    (e: 'manual'): void;
  }>();

  const cameraInput = ref<HTMLInputElement | null>(null);
  const fileInput = ref<HTMLInputElement | null>(null);
  const dragging = ref(false);
  /** dragenter/dragleave also fire for the card's children. */
  let dragDepth = 0;

  function openCamera() {
    if (!props.disabled) cameraInput.value?.click();
  }

  function openFiles() {
    if (!props.disabled) fileInput.value?.click();
  }

  function onPicked(event: Event, origin: TCaptureOrigin) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    const files = Array.from(input.files ?? []);
    // Picking the same file again must fire `change` again.
    input.value = '';
    if (files.length > 0) emit('files', files, origin);
  }

  function onDragEnter() {
    if (props.disabled) return;
    dragDepth++;
    dragging.value = true;
  }

  function onDragLeave() {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) dragging.value = false;
  }

  function onDrop(event: DragEvent) {
    dragDepth = 0;
    dragging.value = false;
    if (props.disabled) return;
    const files = Array.from(event.dataTransfer?.files ?? []);
    if (files.length > 0) emit('files', files, 'gallery');
  }
</script>

<template>
  <div class="v2-menu-source-cards" :class="{ 'is-phone': phone }">
    <template v-if="aiEnabled">
      <button
        v-if="phone"
        type="button"
        class="source-card"
        :disabled="disabled"
        @click="openCamera"
      >
        <span class="card-icon" aria-hidden="true">
          <PosUiIcon name="camera-outline" />
        </span>
        <span class="card-title">{{
          $t('menuImport.source.photosTitle')
        }}</span>
        <span class="card-text">{{ $t('menuImport.source.photosText') }}</span>
        <PosUiBadge
          class="card-badge"
          :label="$t('menuImport.source.recommended')"
        />
      </button>
      <button
        type="button"
        class="source-card is-drop"
        :class="{ 'is-dragging': dragging }"
        :disabled="disabled"
        @click="openFiles"
        @dragenter.prevent="onDragEnter"
        @dragover.prevent
        @dragleave="onDragLeave"
        @drop.prevent="onDrop"
      >
        <span class="card-icon" aria-hidden="true">
          <PosUiIcon name="upload-outline" />
        </span>
        <span class="card-title">{{ $t('menuImport.source.fileTitle') }}</span>
        <span class="card-text">{{
          phone
            ? $t('menuImport.source.fileTextPhone')
            : $t('menuImport.source.fileTextDesktop')
        }}</span>
        <PosUiBadge
          v-if="!phone"
          class="card-badge"
          :label="$t('menuImport.source.recommended')"
        />
      </button>
      <button
        v-if="!phone"
        type="button"
        class="source-card"
        :disabled="disabled"
        @click="emit('paste')"
      >
        <span class="card-icon" aria-hidden="true">
          <PosUiIcon name="keyboard-outline" />
        </span>
        <span class="card-title">{{ $t('menuImport.source.typeTitle') }}</span>
        <span class="card-text">{{ $t('menuImport.source.typeText') }}</span>
      </button>
    </template>
    <button
      v-else
      type="button"
      class="source-card"
      :disabled="disabled"
      @click="emit('manual')"
    >
      <span class="card-icon" aria-hidden="true">
        <PosUiIcon name="pencil-outline" />
      </span>
      <span class="card-title">{{ $t('menuImport.source.manualTitle') }}</span>
      <span class="card-text">{{ $t('menuImport.source.manualText') }}</span>
    </button>
    <input
      ref="cameraInput"
      class="file-input"
      type="file"
      accept="image/*"
      capture="environment"
      tabindex="-1"
      aria-hidden="true"
      @change="onPicked($event, 'camera')"
    />
    <input
      ref="fileInput"
      class="file-input"
      type="file"
      multiple
      :accept="FILE_ACCEPT"
      tabindex="-1"
      aria-hidden="true"
      @change="onPicked($event, 'gallery')"
    />
  </div>
</template>

<style scoped lang="scss">
  .v2-menu-source-cards {
    @apply tw-grid tw-gap-3;
    grid-template-columns: repeat(auto-fit, minmax(min(18rem, 100%), 1fr));
    &.is-phone {
      grid-template-columns: minmax(0, 1fr);
    }
    .source-card {
      @apply tw-relative tw-flex tw-min-h-24 tw-w-full tw-cursor-pointer tw-flex-col tw-items-start tw-gap-1 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-p-4 tw-text-left tw-transition-colors tw-duration-150 hover:tw-border-neutral-300 md:tw-p-5;
      &:focus-visible {
        @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
      &:disabled {
        @apply tw-cursor-not-allowed tw-opacity-60 hover:tw-border-neutral-200;
      }
      // The drop zone: dashed until something is dragged over it.
      &.is-drop {
        @apply tw-border-dashed tw-border-neutral-300 tw-bg-neutral-50;
        &.is-dragging {
          @apply tw-border-neutral-400 tw-bg-white;
        }
      }
    }
    .card-icon {
      @apply tw-mb-2 tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-bg-neutral-100 tw-text-xl tw-text-neutral-700;
    }
    .card-title {
      @apply tw-text-base tw-font-semibold tw-text-neutral-900;
    }
    .card-text {
      @apply tw-text-sm tw-text-neutral-500;
    }
    // Top-right; the title never runs under it (the icon row is above).
    .card-badge {
      @apply tw-absolute tw-right-3 tw-top-3 md:tw-right-4 md:tw-top-4;
    }
    .file-input {
      @apply tw-hidden;
    }
  }
</style>
